import React, { useEffect, useState } from "react";
import {
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Stack,
    TextField,
} from "@fluentui/react";
import useAxios from "axios-hooks";
import { axiosInstance } from "../../../../api/config";
import env from "../../../../env";

const { REACT_APP_API_BASE_URI } = env;

export const buildLookupPayload = (term) => {
    const value = term.trim();
    if (/^\d+$/.test(value)) {
        return { MemberID: Number(value) };
    }
    if (value.includes("@")) {
        return { Username: value, AltEmail: value };
    }
    return { Last: value };
};

const MemberSearch = ({ initialTerm, onSelect }) => {
    const [term, setTerm] = useState(initialTerm || "");
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);

    const [{ loading }, lookup] = useAxios(
        {
            url: "2020-05-05/user/lookup",
            method: "POST",
            baseURL: REACT_APP_API_BASE_URI,
        },
        { axios: axiosInstance, manual: true }
    );

    const runSearch = async (value) => {
        if (!value || !value.trim()) {
            return;
        }

        setError(null);
        try {
            const response = await lookup({ data: buildLookupPayload(value) });
            const members = Array.isArray(response.data) ? response.data : [];
            setResults(members);
            if (members.length === 1) {
                onSelect(members[0]);
            }
        } catch (err) {
            setResults(null);
            setError("Member lookup failed. Please try again.");
        }
    };

    // Deep-link search runs once on mount only.
    useEffect(() => {
        if (initialTerm) {
            runSearch(initialTerm);
        }
    }, []);

    const columns = [
        {
            key: "select",
            name: "",
            minWidth: 80,
            maxWidth: 80,
            onRender: (item) => <DefaultButton text="Select" onClick={() => onSelect(item)} />,
        },
        { key: "memberID", name: "Member ID", fieldName: "memberID", minWidth: 80, maxWidth: 100 },
        {
            key: "name",
            name: "Name",
            minWidth: 140,
            maxWidth: 200,
            onRender: (item) => `${item.first || ""} ${item.last || ""}`.trim(),
        },
        { key: "username", name: "Username", fieldName: "username", minWidth: 200, maxWidth: 300 },
        { key: "cb", name: "Cobrand", fieldName: "cb", minWidth: 120, maxWidth: 200 },
        { key: "cbid", name: "CBID", fieldName: "cbid", minWidth: 60, maxWidth: 80 },
    ];

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                <TextField
                    label="Find member"
                    placeholder="Member ID, username/email, or last name"
                    value={term}
                    onChange={(e, value) => setTerm(value || "")}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            runSearch(term);
                        }
                    }}
                    styles={{ root: { width: 380 } }}
                />
                <PrimaryButton text="Search" onClick={() => runSearch(term)} disabled={loading || !term.trim()} />
            </Stack>

            {loading && <Spinner size={SpinnerSize.medium} label="Looking up members..." labelPosition="right" />}
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            {!loading && results && results.length === 0 && (
                <MessageBar>No members found.</MessageBar>
            )}
            {!loading && results && results.length > 1 && (
                <DetailsList
                    items={results}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                    compact
                />
            )}
        </Stack>
    );
};

export default MemberSearch;
